/**
 * Pure projection helpers for the welcome-card channel pills.
 *
 * Lives in a `.ts` file (no JSX) so the unit tests can import it
 * under `node --experimental-strip-types`. The presentational
 * `<ChannelPills>` Preact component in `welcome.tsx` consumes
 * `buildChannelPills` and only owns icon + DOM rendering.
 *
 * Contracts pinned by the test suite:
 *   - Pills are always returned in the order WhatsApp, email, phone
 *     regardless of which fields the operator filled in. This keeps
 *     the welcome card's silhouette consistent across tenants.
 *   - WhatsApp is ALWAYS inactive (`href === null`) at launch; the
 *     ElevenLabs WhatsApp integration ships in week 8 (CLAUDE.md
 *     sectie 17). Even when the operator pre-populates a number we
 *     do not want to ship a dead wa.me link.
 *   - Email becomes a `mailto:` link when set.
 *   - Phone becomes a `tel:` link with all non-numerics stripped so
 *     iOS / Android dialers parse the value reliably; the visible
 *     label keeps the operator's preferred formatting.
 */

import type { WidgetChannelsConfig } from "./types";

export interface ChannelPillSpec {
  key: "whatsapp" | "email" | "phone";
  label: string;
  /** Set when the pill is interactive; null means greyed-out. */
  href: string | null;
  /**
   * Secondary line rendered in muted text beneath `label`. For active
   * pills this is the actual address (e.g. "support@bazaaronline.nl")
   * which doubles as a customer-trust signal (matches Watermelon's
   * pattern). For the always-inactive WhatsApp pill this carries the
   * "Binnenkort beschikbaar" microcopy. Null means the welcome-card
   * renderer should fall back to the generic unavailable label.
   */
  sublabel: string | null;
}

export interface ChannelLabels {
  whatsapp: string;
  email: string;
  phone: string;
  /** Microcopy for pills that are not yet live (currently WhatsApp). */
  unavailable: string;
}

export function buildChannelPills(
  channels: WidgetChannelsConfig,
  labels: ChannelLabels,
): ChannelPillSpec[] {
  return [
    {
      key: "whatsapp",
      label: labels.whatsapp,
      href: null,
      // WhatsApp is unconditionally inactive at launch (see file
      // docstring). The sublabel is the "Binnenkort beschikbaar"
      // microcopy rather than the raw number, even when the operator
      // pre-populated one, so we never leak an unannounced number.
      sublabel: labels.unavailable,
    },
    {
      key: "email",
      label: labels.email,
      href: channels.email ? `mailto:${channels.email}` : null,
      // Trust signal: visitors see the actual address inline. When
      // unset the renderer substitutes the generic unavailable label.
      sublabel: channels.email ?? null,
    },
    {
      key: "phone",
      label: labels.phone,
      href: channels.phone ? `tel:${channels.phone.replace(/[^+\d]/g, "")}` : null,
      // Display the operator-formatted value verbatim so e.g.
      // "+31 20 123 4567" stays readable; the href strips spacing
      // separately so dialers parse the value reliably.
      sublabel: channels.phone ?? null,
    },
  ];
}
